/**
 * Shared premium Ranked chrome (Season Table V7): page backdrop, header
 * (BACK + title/subtitle) and RANKED / TABLE / WEEKLY CUP tab strip.
 * Used by RankedLeaderboardScreen and WeeklyCupScreen so the header and
 * tabs keep identical geometry across both screens.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

export const SEASON_TABLE_ASSETS = '/kickard/season-table';

const TABS = [
  ['ranked', 'Ranked', '/ranked'],
  ['table', 'Table', '/ranked/leaderboard'],
  ['weekly-cup', 'Weekly Cup', '/ranked/weekly-cup'],
];

// Runtime asset URLs exposed as CSS vars (public/ assets, not bundled).
const ASSET_VARS = Object.fromEntries([
  'btn_back', 'btn_ranked', 'btn_table_active', 'btn_weekly',
  'btn_table_inactive', 'btn_weekly_active', 'btn_ranked_active', 'btn_home',
  'progress_gold', 'progress_gray', 'progress_orange', 'progress_track',
  'row_normal', 'row_rank1', 'row_rank2', 'row_rank3', 'row_selected',
  'stadium_header', 'stadium_page',
].map((n) => [`--kst-${n.replace(/_/g, '-')}`, `url(${SEASON_TABLE_ASSETS}/${n}.webp)`]));

export function RankedBackdrop({ children, testid }) {
  return (
    <div className="kst" data-testid={testid} style={ASSET_VARS}>
      <div className="kst__bg-page" aria-hidden />
      <div className="kst__bg-header" aria-hidden />
      {children}
    </div>
  );
}

// Canonical Home action — same route Match Summary / GameScreen use.
export const HOME_PATH = '/home';

export function RankedHeader({ title, subtitle, onBack, backTestId, titleTestId }) {
  const navigate = useNavigate();
  return (
    <header className="kst__header">
      <button onClick={onBack} className="kst__back" data-testid={backTestId} aria-label="Back" />
      <div className="kst__titles">
        <h1 className="kst__title" data-testid={titleTestId}>{title}</h1>
        <div className={`kst__subtitle${subtitle.length > 26 ? ' kst__subtitle--long' : ''}`}>{subtitle}</div>
      </div>
      <button
        onClick={() => navigate(HOME_PATH)}
        className="kst__home"
        data-testid="ranked-chrome-home"
        aria-label="Home"
      />
    </header>
  );
}

// Season Table keeps its original tab art/classes; only the non-table
// active state swaps to the derived V7 states.
function tabClass(id, active) {
  if (id === 'ranked' && active === 'ranked') return 'kst__tab kst__tab--ranked-on';
  if (id === 'table' && active !== 'table') return 'kst__tab kst__tab--table-off';
  if (id === 'weekly-cup' && active === 'weekly-cup') return 'kst__tab kst__tab--weekly-cup-on';
  return `kst__tab kst__tab--${id}`;
}

export function RankedTabs({ active, onNavigate }) {
  return (
    <div className="kst__tabs" role="tablist" data-testid="competitive-tabs">
      {TABS.map(([id, label, path]) => (
        <button
          key={id}
          role="tab"
          aria-selected={id === active}
          aria-label={label}
          data-testid={`competitive-tab-${id}`}
          onClick={() => onNavigate(path)}
          className={tabClass(id, active)}
        />
      ))}
    </div>
  );
}
